import { CollapsibleSearch } from "$root/components/CollapsibleSearch";
import { Divider } from "$root/components/Divider";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { seconds } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import {
	AutoTooltip,
	ButtonGroupRadio,
	type ButtonGroupRadioProps,
	Icon,
	type IconProps,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import {
	type DataAttributesProps,
	type DropdownDropdownItem,
	type DropdownTriggerProps,
	pickDataAttrs,
	DropdownMenu,
	RectangularCounter,
	Button,
} from "@mdotm/shadcn/mdotm/components";
import { asyncStateFrom } from "@mdotm/utils/promise";
import { ForEach } from "@mdotm/utils/react-extra";
import { type ReactNode, useMemo } from "react";
import {
	type WorkspaceEntityManagementActions,
	workspaceEntityManagementActions,
	type WorkspaceFolderManagementActions,
	workspaceFolderManagementActions,
} from "./entity-management";
import { workspaceRepository } from "./repository";
import { useWorkspaceSlices, useWorkspaceStore } from "./state";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { SavedAtTaskQueueIndicator } from "$root/components/SavedAtTaskQueueIndicator";

type WorkspaceToolbarProps<TTabs> = {
	total: number;
	filtered: number;
	label: string;
	actions?: Array<WorkspaceToolbarActionItem | "separator">;
	moreActions?: Array<DropdownDropdownItem>;
} & (
	| {
			search: string;
			onChangeSearch(newSearch: string): void;
	  }
	| {
			search?: undefined;
			onChangeSearch?: undefined;
	  }
) &
	(
		| {
				tabValue: ButtonGroupRadioProps<TTabs>["value"];
				onChangeTab: ButtonGroupRadioProps<TTabs>["onChange"];
				tabOptions: ButtonGroupRadioProps<TTabs>["options"];
		  }
		| {
				tabValue?: undefined;
				onChangeTab?: undefined;
				tabOptions?: undefined;
		  }
	);

function WorkspaceToolBar<TTabs = string>(props: WorkspaceToolbarProps<TTabs>) {
	const { push } = useTypedNavigation();
	const workspaceStore = useWorkspaceStore();
	// The three fields this needs are the *document's*, so it subscribes to the document rather than to the
	// serialized workspace: `useSmartWorkspace` re-serialized every chart and column on every atom change to
	// read a name off the result.
	const upstreamWorkspace = useAtomValue(useWorkspaceSlices().document$);

	const uuid = upstreamWorkspace?.uuid;
	const { data: defaultWorkspace } = workspaceRepository.getDefaultWorkspace.useQuery();
	const workspaceType = defaultWorkspace?.uuid !== uuid ? "CUSTOM" : "DEFAULT";

	const workspaceManagementActions = workspaceEntityManagementActions(
		{
			// Non-null exactly where `useSmartWorkspace().name!` was: an unsaved workspace has no document, and
			// the actions built from it are the ones the "DEFAULT" branch does not offer.
			name: upstreamWorkspace?.name as string,
			taxonomy: upstreamWorkspace?.taxonomy as string[],
			uuid: upstreamWorkspace?.uuid as string,
		},
		{
			onDelete() {
				push("PortfolioStudio/DefaultWorkspace", {});
			},
			onDuplicate(uuid) {
				push("PortfolioStudio/Workspace", { id: uuid });
			},
			onCreate(uuid) {
				push("PortfolioStudio/Workspace", { id: uuid });
			},
		},
	);

	const folderManagementAction = workspaceFolderManagementActions({
		name: "Workspace example",
		taxonomy: ["ROOT", "Workspace example"],
	});

	const moreActions = useMemo(
		() =>
			workspaceType === "CUSTOM"
				? workspaceMoreActions.custom(workspaceManagementActions)
				: workspaceMoreActions.default(workspaceManagementActions, folderManagementAction),
		[workspaceManagementActions, folderManagementAction, workspaceType],
	);

	return (
		<Card className="grow-0 shrink-0 border border-solid border-[var(--palette-N100)]">
			<Row alignItems="center" justifyContent="space-between">
				<Row alignItems="center" gap={8} flexGrow={0}>
					{props.tabValue && props.tabOptions && props.tabOptions && (
						<ButtonGroupRadio
							options={props.tabOptions}
							value={props.tabValue}
							onChange={props.onChangeTab}
							size="small"
						/>
					)}
					<Row gap={8}>
						{props.onChangeSearch && (
							<CollapsibleSearch
								query={props.search}
								onQueryChange={props.onChangeSearch}
								label="Search portfolio by name"
								placeholder="Search portfolio by name"
								timeout={seconds(0.5)}
								data-qualifier="Workspace/search"
							/>
						)}
						<Row alignItems="center">
							<Text type="Body/L/Book" as="p">
								{props.label}:{" "}
								<Text type="Body/L/Bold" as="span">
									{props.filtered}
								</Text>{" "}
								of {props.total}
							</Text>
						</Row>
					</Row>
				</Row>
				<Divider direction="vertical" className="h-[17px]" />
				<Row alignItems="center" gap={16} flexGrow={0}>
					<ForEach collection={props.actions ?? []}>
						{(action) =>
							action === "separator" ? (
								<Divider direction="vertical" className="h-[17px]" />
							) : (
								<AutoTooltip
									mode="hover"
									disabled={!action.tooltip}
									severity="success"
									align="startToStart"
									position="top"
									trigger={({ innerRef }) => <WorkspaceToolbarAction {...action} innerRef={innerRef} />}
								>
									{action.tooltip?.label}
								</AutoTooltip>
							)
						}
					</ForEach>
				</Row>
				<Divider direction="vertical" className="h-[17px]" />
				<Row alignItems="center" gap={8} flexGrow={0}>
					<SavedAtTaskQueueIndicator
						className="mr-4"
						taskQueueState$={workspaceStore.updateQueueState$}
						onRetry={workspaceStore.retrySave}
					/>
					<DropdownMenu Trigger={MoreActionsButton} actions={moreActions} />
				</Row>
			</Row>
		</Card>
	);
}

type WorkspaceToolbarActionItem = {
	onClick?(): void;
	onClickAsync?(): Promise<void>;
	dot?: { counter: number } | boolean;
	disabled?: boolean;
	tooltip?: { label: string };
	icon?: Pick<IconProps, "icon" | "color" | "size">;
	children?: ReactNode;
} & DataAttributesProps;
type WorkspaceToolbarActionProps = WorkspaceToolbarActionItem & {
	innerRef?: (instance: HTMLElement | null) => void;
};

function WorkspaceToolbarAction(props: WorkspaceToolbarActionProps) {
	return (
		<Button
			{...(props.onClickAsync ? { onClickAsync: props.onClickAsync } : { onClick: props.onClick })}
			palette="ghost"
			size="small"
			disabled={props.disabled}
			innerRef={props.innerRef}
			{...pickDataAttrs(props)}
		>
			{props.icon && <Icon {...props.icon} size={18} color={themeCSSVars.palette_N500} />} {props.children}
			{props.dot != null && typeof props.dot !== "boolean" && <RectangularCounter count={props.dot.counter} />}
		</Button>
	);
}

const workspaceMoreActions = {
	default: (
		workspaceManagementActions: WorkspaceEntityManagementActions,
		workspaceFolderManagementActions: WorkspaceFolderManagementActions,
	): DropdownDropdownItem[] => [
		{
			"data-qualifier": "PortfolioStudio/Portfolios/MoreActions/CreateFolder",
			disabled: !workspaceFolderManagementActions.createAsync,
			label: "New folder",
			icon: "folder",
			onClick() {
				workspaceFolderManagementActions.createAsync().catch(noop);
			},
		},
		{
			"data-qualifier": "PortfolioStudio/Portfolios/MoreActions/CreateWorkspace",
			disabled: !workspaceManagementActions.createAsync,
			label: "New workspace",
			icon: "workspace",
			onClick() {
				if (!workspaceManagementActions.createAsync) {
					return;
				}
				workspaceManagementActions.createAsync().catch(noop);
			},
		},
	],
	custom: (workspaceManagementActions: WorkspaceEntityManagementActions): DropdownDropdownItem[] => [
		{
			"data-qualifier": "PortfolioStudio/Portfolios/Workspace/MoreActions/Rename",
			disabled: !workspaceManagementActions.renameAsync,
			label: "Rename",
			icon: "Edit",
			onClick() {
				workspaceManagementActions.renameAsync?.().catch(noop);
			},
		},
		{
			"data-qualifier": "PortfolioStudio/Portfolios/Workspace/MoreActions/Duplicate",
			disabled: !workspaceManagementActions.duplicateAsync,
			label: "Duplicate",
			icon: "Content-Copy",
			onClick() {
				workspaceManagementActions.duplicateAsync?.().catch(noop);
			},
		},
		{
			"data-qualifier": "PortfolioStudio/Portfolios/Workspace/MoreActions/MoveToFolder",

			disabled: !workspaceManagementActions.moveToFolder,
			label: "Move to folder",
			icon: "Drag-and-drop",
			onClick() {
				workspaceManagementActions.moveToFolder?.().catch(noop);
			},
		},
		{
			"data-qualifier": "PortfolioStudio/Portfolios/Workspace/MoreActions/Delete",
			disabled: !workspaceManagementActions.deleteAsync,
			label: "Delete",
			icon: "Delete",
			onClick() {
				workspaceManagementActions.deleteAsync?.().catch(noop);
			},
		},
	],
};

const MoreActionsButton = ({ state, ...props }: DropdownTriggerProps) => (
	<Button
		{...props}
		state={asyncStateFrom(state)}
		progressBar={false}
		size="small"
		palette="secondary"
		data-qualifier="PortfolioStudio/Portfolios/MoreActions"
	>
		<Icon icon="More" className="rotate-z-90" size={18} />
	</Button>
);

export default WorkspaceToolBar;
